import type { ShallowRef } from 'vue';
import type { AfDate, OId } from '@Types';
import type { Donation } from '@DonationModule';
import type { ProductAtCart } from '@ProductsAndServicesModule';
import type { RawField } from '@FormModule';
import { CART_TYPES } from './enums';

enum CART_STEPS {
  ADD_PEOPLE = 'step-add-people',
  CHECKOUT_METHOD = 'step-checkout-method',
  DONATION = 'step-donation',
  MOBILE_CHECKOUT = 'step-mobile-checkout',
  MOBILE_PROCESSING = 'step-mobile-processing',
  OUTCOME_INFO = 'step-outcome-info',
  PAYMENT_METHOD = 'step-payment-method',
  PRODUCTS_AND_SERVICES = 'products-and-services',
  PRODUCTS_INFO = 'step-products-info',
  SUMMARY = 'step-summary',
}

enum OUTCOMES {
  CITATION = 'citation',
  DONATION = 'donation',
  FOUND = 'found',
  LOST = 'lost',
  LOST_AND_FOUND = 'lost_found',
  OUTCOME = 'outcome',
  PRODUCT = 'product',
  REHOME = 'rehome',
}

// NOTE: Do not sort this enum
enum OUTCOMES_TYPES {
  // IN CARE
  ADOPTED = 'adopted',
  CLINIC_OUT = 'clinic_out',
  DIED_IN_CARE = 'died_in_care',
  DOA = 'doa',
  LOST = 'lost',
  OWNER_REQUESTED_EUTHANASIA = 'owner_requested_euthanasia',
  RETURN_TO_FIELD = 'return_to_field',
  RETURN_TO_OWNER = 'return_to_owner',
  SERVICE_OUT = 'service_out',
  SHELTER_EUTHANASIA = 'shelter_euthanasia',
  TRANSFER_OUT = 'transfer_out',
  WILDLIFE_OUT = 'wildlife_out',

  // LOST & FOUND
  FINDER_KEPT = 'finder_kept',
  REHOMED = 'rehomed',
  REUNITED_WITH_OWNER = 'reunited_with_owner',
  SHELTER_INTAKE = 'shelter_intake',
  UNKNOWN = 'unknown',

  // REHOME
  REHOME = 'rehome',
  REHOME_SHELTER_INTAKE = 'rehome_shelter_intake',
  REHOME_UNKNOWN = 'rehome_unknown',
  REMAINED_WITH_OWNER = 'remained_with_owner',

  // CITATIONS
  CITATION = 'citation',

  // PRODUCTS
  PRODUCTS = 'product',

  // INTAKE
  INTAKE = 'intake',

  // DONATION
  DONATIONS = 'donation',
}

enum PAYMENT_METHODS {
  CARD_READER = 'card_reader',
  CASH = 'cash',
  CHECK = 'check',
  CREDIT = 'credit_card',
  OTHER = 'other',
}

enum PAYMENT_STATUS {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  DELETED = 'deleted',
  PAID = 'paid',
  PENDING_OFFSITE_PAYMENT = 'pending_offsite_payment',
  PENDING = 'pending',
}

type AfSource = 'manual' | 'mobile_checkout' | 'adopt_app';
type CartType = `${CART_TYPES}`;
type DiscountType = 'percent' | 'amount';
type IntakeType = 'stray' | 'owner_surrender';
type OutcomeType = 'adopted' | 'return_to_owner';
type PaymentStatus =
  | PAYMENT_STATUS.ACTIVE
  | PAYMENT_STATUS.COMPLETED
  | PAYMENT_STATUS.DELETED
  | PAYMENT_STATUS.PAID
  | PAYMENT_STATUS.PENDING;
type PaymentType =
  | PAYMENT_METHODS.CARD_READER
  | PAYMENT_METHODS.CASH
  | PAYMENT_METHODS.CHECK
  | PAYMENT_METHODS.CREDIT
  | PAYMENT_METHODS.OTHER;
type ProductSource = 'Stray' | 'Return to Owner';
type RtoFeeType = 'daily' | 'flat';
type CartCategory =
  | 'Adoptions'
  | 'Boardings'
  | 'Citations'
  | 'Donations'
  | 'Owner Surrenders'
  | 'Products & Services'
  | 'Returns to Owner'
  | 'Strays';

interface AdditionalFee {
  _id: string;
  amount: string | number;
  description: string;
  label?: string;
  outcome_type: string; // TODO : AF-533 This should be an ENUM
  species: string; // TODO : AF-533 This should be an ENUM
}

interface RtoFee extends Omit<AdditionalFee, '_id'> {
  _id: OId;
  shelter_id?: OId;
  status: string;
  type: RtoFeeType;
}

interface StepHeader {
  icon: string;
  title: string;
  subtitle: string;
}

interface Step {
  active: boolean;
  component: ShallowRef<any>;
  header: StepHeader;
  name: CART_STEPS;
  tooltip: string;
}

// TODO AF-532 :: Improve this interface
interface CartItem {
  [key: string]: any;
  _id: string;
  animal_name: string;
  photo: string;
}

interface CashDrawer {
  _id: OId;
  label: string;
  status: string;
  order: number;
}

interface PaymentMethod {
  disabled?: boolean;
  disabledMessage?: string;
  icon: string;
  key: PaymentType;
  label: string;
  value: PaymentType;
}

interface PreAdoptPayload {
  cart_id: string;
  custom_outcome_date: string;
  custom_outcome_type: CustomOutcomeType;
  last_four_digits?: string | number;
  outcome_date: string;
  payment_type: PaymentType;
  pre_adopt_date: string;
}

interface EditPreAdoptPayload {
  cart_id: string;
  custom_outcome_date: string;
  last_four_digits?: string | number;
  payment_type: PaymentType;
}

interface PendingMobileIntakePayload {
  cart_id: string;
  cash_drawer_id?: string;
  cash_drawer?: string;
}

interface OutcomeForm {
  [key: string]: any;
  _id: OId;
  disabled?: boolean;
  disabledMessage?: string;
  fields: RawField[];
  form_name: string;
  form_title: string;
  form_type: string;
  is_default: boolean;
  shelter_id: OId;
  status: string;
  updated_at: AfDate;
}

// TODO AF-532 :: Improve this interface
interface TempCart {
  [key: string]: any;
  _id: OId;
  cart_type: CartType;
  expires_at: AfDate;
  is_expired: boolean;
  paid?: boolean;
  status: PaymentStatus;
}

type CartItemType = 'animal' | 'citation' | 'donation' | 'product' | 'service';

interface AddProductToCartRequestPayload {
  cart_type: 'product';
  products: ProductAtCart[];
}

interface DonationAtCart extends Donation {
  category: string;
  date: string;
  notes?: string;
}

interface AddDonationToCartRequestPayload {
  cart_type: 'donation';
  donation: DonationAtCart;
}

interface AddDonationWithDonorToCartRequestPayload {
  cart_type: 'donation';
  donation: DonationAtCart;
  people: any; // TODO TASK-533 - People should have an interface
}

interface AddProductToOutcomeCartRequestPayload {
  products: ProductAtCart[];
}

enum CUSTOM_OUTCOME_TYPE {
  PRE_ADOPT = 'pre_adopt',
  OFFSITE = 'offsite',
}

type CustomOutcomeType = `${CUSTOM_OUTCOME_TYPE}`;

export type {
  AddDonationToCartRequestPayload,
  AddDonationWithDonorToCartRequestPayload,
  AddProductToCartRequestPayload,
  AddProductToOutcomeCartRequestPayload,
  AdditionalFee,
  CartItem,
  CartItemType,
  DonationAtCart,
  EditPreAdoptPayload,
  OutcomeForm,
  PaymentMethod,
  PendingMobileIntakePayload,
  PreAdoptPayload,
  RtoFee,
  Step,
  TempCart,
};

export { CART_STEPS, CUSTOM_OUTCOME_TYPE, OUTCOMES, OUTCOMES_TYPES, PAYMENT_METHODS, PAYMENT_STATUS };

export type {
  AfSource,
  CashDrawer,
  CartCategory,
  CartType,
  CustomOutcomeType,
  DiscountType,
  IntakeType,
  OutcomeType,
  PaymentStatus,
  PaymentType,
  ProductSource,
  RtoFeeType,
};
